import React, { useState, useEffect } from "react";
import { useTermination } from "@/contexts/TerminationContext";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format, addDays, differenceInDays, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { TerminationReason, NoticeCalculation } from "@/types/termination";
import { UseFormReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

// Define the checkbox fields explicitly to help TypeScript
type NoticeOptionField = "workingNoticePeriod" | "paymentInLieuOfNotice";

const TERMINATION_REASONS = [
  { id: TerminationReason.RESIGNATION, label: "Resignation" },
  { id: TerminationReason.MUTUAL_AGREEMENT, label: "Mutual agreement" },
];

interface CombinedDetailsStepProps {
  employeeId?: string;
  employeeStartDate?: string;
  form: UseFormReturn<any>;
}

// Helper function to ensure notice period fields are set correctly
const ensureNoticePeriodDates = (
  calculation: NoticeCalculation,
): NoticeCalculation => {
  // Create a deep copy to avoid reference issues
  const result = JSON.parse(JSON.stringify(calculation)) as NoticeCalculation;

  // Backend requires dates to be in YYYY-MM-DD format and doesn't accept null values
  // Get current termination date to use as fallback
  const today = format(new Date(), "yyyy-MM-dd");

  // Set notice_period_start_date and notice_period_end_date based on selected option
  if (result.workingNoticePeriod) {
    // Ensure we have valid date strings
    result.notice_period_start_date = result.notice_period_start_date || today;
    result.notice_period_end_date =
      result.notice_period_end_date || result.payThroughDate || today;
  } else if (result.paymentInLieuOfNotice) {
    // For PILON, use termination date for both (since no notice period is worked)
    const terminationDate = result.payThroughDate
      ? format(new Date(result.payThroughDate), "yyyy-MM-dd")
      : today;

    result.notice_period_start_date = terminationDate;
    result.notice_period_end_date = terminationDate;
  } else {
    // Default case - ensure we have valid dates
    result.notice_period_start_date = today;
    result.notice_period_end_date = today;
  }

  console.log("Sanitized dates for API:", {
    start: result.notice_period_start_date,
    end: result.notice_period_end_date,
  });

  return result;
};

const CombinedDetailsStep: React.FC<CombinedDetailsStepProps> = ({
  employeeId,
  employeeStartDate,
  form,
}) => {
  const {
    currentTermination,
    setTerminationReason,
    setTerminationDate,
    updateTermination,
  } = useTermination();

  const [noticeCalculation, setNoticeCalculation] = useState<NoticeCalculation>(
    {
      requiredNoticeDays: 0,
      payThroughDate: "",
      workingNoticePeriod: true,
      paymentInLieuOfNotice: false,
      notice_period_start_date: null,
      notice_period_end_date: null,
    },
  );

  // Initialize local state from context
  useEffect(() => {
    if (currentTermination?.noticeCalculation) {
      setNoticeCalculation(currentTermination.noticeCalculation);
    } else if (currentTermination?.termination_date) {
      // Calculate default notice period based on length of service
      // This is a simplified calculation - in a real app, this would be more complex
      const startDate = currentTermination.employmentStartDate
        ? new Date(currentTermination.employmentStartDate)
        : employeeStartDate
          ? new Date(employeeStartDate)
          : new Date(); // Fallback

      const terminationDate = new Date(
        currentTermination.termination_date + "T00:00:00",
      );
      const yearsOfService = Math.floor(
        (terminationDate.getTime() - startDate.getTime()) /
          (365.25 * 24 * 60 * 60 * 1000),
      );

      // UK statutory minimum is 1 week per year of service, up to 12 weeks
      const statutoryNoticeDays = Math.min(yearsOfService, 12) * 7;
      // Minimum notice is 1 week (7 days)
      const requiredNoticeDays = Math.max(7, statutoryNoticeDays);

      const payThroughDate = format(
        addDays(terminationDate, requiredNoticeDays),
        "yyyy-MM-dd",
      );

      const terminationDateStr = format(terminationDate, "yyyy-MM-dd");

      // For working notice period, notice_period_start_date is termination_date and notice_period_end_date is payThroughDate
      const newNoticeCalculation: NoticeCalculation = {
        requiredNoticeDays,
        payThroughDate,
        workingNoticePeriod: true,
        paymentInLieuOfNotice: false,
        notice_period_start_date: terminationDateStr,
        notice_period_end_date: payThroughDate,
      };

      setNoticeCalculation(newNoticeCalculation);

      // Only update if we haven't already created a notice calculation
      if (currentTermination.id && !noticeCalculation.payThroughDate) {
        // Log what we're trying to update
        console.log("Initial update with notice period dates:", {
          start: newNoticeCalculation.notice_period_start_date,
          end: newNoticeCalculation.notice_period_end_date,
        });

        // Ensure proper fields for the backend
        const cleanedCalculation =
          ensureNoticePeriodDates(newNoticeCalculation);

        // Get sanitized dates
        const startDate =
          cleanedCalculation.notice_period_start_date ||
          format(new Date(), "yyyy-MM-dd");
        const endDate =
          cleanedCalculation.notice_period_end_date ||
          format(new Date(), "yyyy-MM-dd");

        console.log("Sending direct update with dates:", startDate, endDate);

        // The backend expects these fields at the top level, not nested under noticeCalculation
        updateTermination(currentTermination.id, {
          termination_date: terminationDateStr, // Use the termination date string we calculated above
          notice_period_start_date: startDate,
          notice_period_end_date: endDate,
          is_payment_in_lieu_of_notice:
            cleanedCalculation.paymentInLieuOfNotice,
          notice_calculation: {
            requiredNoticeDays: cleanedCalculation.requiredNoticeDays,
            payThroughDate: cleanedCalculation.payThroughDate,
            workingNoticePeriod: cleanedCalculation.workingNoticePeriod,
            paymentInLieuOfNotice: cleanedCalculation.paymentInLieuOfNotice,
          },
        });
      }
    }
  }, [
    currentTermination?.id,
    currentTermination?.termination_date,
    currentTermination?.employmentStartDate,
    currentTermination?.noticeCalculation,
    employeeStartDate,
    updateTermination,
  ]);

  // Update form whenever notice calculation changes
  useEffect(() => {
    if (noticeCalculation.payThroughDate) {
      form.setValue("payThroughDate", noticeCalculation.payThroughDate);
    }
    if (noticeCalculation.requiredNoticeDays) {
      form.setValue("requiredNoticeDays", noticeCalculation.requiredNoticeDays);
    }
  }, [form, noticeCalculation]);

  const handleTerminationReasonChange = (value: string) => {
    console.log("Selected termination reason:", value);
    setTerminationReason(value as TerminationReason);

    // Update form value as well
    form.setValue("terminationReason", value);
  };

  const handleTerminationDateChange = (date: Date | undefined) => {
    if (!date || !currentTermination?.id) return;

    // Format date using ISO string to get YYYY-MM-DD format without timezone issues
    const newTerminationDate = date.toISOString().split("T")[0];

    // Log the change for debugging
    console.log(`Setting termination date to: ${newTerminationDate}`);

    // Update the UI state first (this won't make an API call)
    setTerminationDate(newTerminationDate);

    const updatedCalculation = { ...noticeCalculation };

    if (updatedCalculation.workingNoticePeriod) {
      // For Working Notice:
      // - notice_period_start_date = today
      // - notice_period_end_date = termination_date (last working day)
      // - pay through date = termination_date (exactly)
      const today = format(new Date(), "yyyy-MM-dd");
      updatedCalculation.notice_period_start_date = today;
      updatedCalculation.notice_period_end_date = newTerminationDate;
      updatedCalculation.payThroughDate = newTerminationDate;
    } else if (updatedCalculation.paymentInLieuOfNotice) {
      // For PILON:
      // - notice_period_start_date = termination_date
      // - notice_period_end_date = termination_date + notice days
      // - pay through date = termination_date + notice days
      updatedCalculation.notice_period_start_date = newTerminationDate;

      // Use a fixed noon time (UTC) to avoid timezone issues
      const dateWithNoonUTC = new Date(`${newTerminationDate}T12:00:00Z`);
      const payThroughDate = format(
        addDays(dateWithNoonUTC, updatedCalculation.requiredNoticeDays),
        "yyyy-MM-dd",
      );
      updatedCalculation.notice_period_end_date = payThroughDate;
      updatedCalculation.payThroughDate = payThroughDate;
    }

    // Update local state
    setNoticeCalculation(updatedCalculation);

    // Ensure proper notice period dates for the API
    const cleanedCalculation = ensureNoticePeriodDates(updatedCalculation);

    // Get sanitized dates
    const startDate =
      cleanedCalculation.notice_period_start_date ||
      new Date().toISOString().split("T")[0];
    const endDate =
      cleanedCalculation.notice_period_end_date ||
      new Date().toISOString().split("T")[0];

    // Make a SINGLE API call with all updated data
    console.log("Sending consolidated update with new termination date:", {
      date: newTerminationDate,
      start: startDate,
      end: endDate,
    });

    // Make one single update with all relevant fields
    updateTermination(currentTermination.id, {
      termination_date: newTerminationDate,
      notice_period_start_date: startDate,
      notice_period_end_date: endDate,
      is_payment_in_lieu_of_notice: cleanedCalculation.paymentInLieuOfNotice,
      notice_calculation: {
        requiredNoticeDays: cleanedCalculation.requiredNoticeDays,
        payThroughDate: cleanedCalculation.payThroughDate || newTerminationDate,
        workingNoticePeriod: cleanedCalculation.workingNoticePeriod,
        paymentInLieuOfNotice: cleanedCalculation.paymentInLieuOfNotice,
      },
    })
      .then(() => {
        console.log("Termination date and notice period updated successfully");
      })
      .catch((error) => {
        console.error("Failed to update termination:", error);
      });
  };

  const handleCheckboxChange =
    (field: NoticeOptionField) => (checked: boolean) => {
      const exclusiveFields: NoticeOptionField[] = [
        "workingNoticePeriod",
        "paymentInLieuOfNotice",
      ];

      // Create a copy of the current calculation
      const updatedCalculation = { ...noticeCalculation };

      if (exclusiveFields.includes(field) && checked) {
        // Uncheck all other exclusive fields
        exclusiveFields.forEach((f) => {
          if (f !== field) {
            // Type assertion to convince TypeScript this is valid
            (updatedCalculation as any)[f] = false;
          }
        });
      }

      // Update the targeted field
      (updatedCalculation as any)[field] = checked;

      console.log("checkbox", currentTermination);
      // Update notice period dates based on selected option
      if (currentTermination?.termination_date) {
        // Use the exact termination date string from the context to avoid timezone issues
        const terminationDateStr = currentTermination.termination_date;

        if (field === "workingNoticePeriod" && checked) {
          // For Working Notice Period:
          // - notice_period_start_date = today
          // - notice_period_end_date = termination_date
          // - pay through date = termination_date (exactly)
          const today = format(new Date(), "yyyy-MM-dd");
          updatedCalculation.notice_period_start_date = today;
          updatedCalculation.notice_period_end_date = terminationDateStr;
          updatedCalculation.payThroughDate = terminationDateStr;
        } else if (field === "paymentInLieuOfNotice" && checked) {
          // For PILON:
          // - notice_period_start_date = termination_date
          // - notice_period_end_date = termination_date + notice days
          // - pay through date = termination_date + notice days
          updatedCalculation.notice_period_start_date = terminationDateStr;

          // Use a fixed noon time (UTC) to avoid timezone issues
          const terminationDate = new Date(`${terminationDateStr}T12:00:00Z`);
          const payThroughDate = format(
            addDays(terminationDate, updatedCalculation.requiredNoticeDays),
            "yyyy-MM-dd",
          );
          updatedCalculation.notice_period_end_date = payThroughDate;
          updatedCalculation.payThroughDate = payThroughDate;
        }
      }

      console.log("Updated notice calculation:", updatedCalculation);
      console.log(
        "Notice start date:",
        updatedCalculation.notice_period_start_date,
      );
      console.log(
        "Notice end date:",
        updatedCalculation.notice_period_end_date,
      );

      // First update the local state
      setNoticeCalculation(updatedCalculation);

      // Then update the termination record
      if (currentTermination?.id) {
        console.log("Updating termination with notice period details:", {
          start: updatedCalculation.notice_period_start_date,
          end: updatedCalculation.notice_period_end_date,
          pilon: field === "paymentInLieuOfNotice" && checked,
        });

        // Ensure proper notice period dates
        const cleanedCalculation = ensureNoticePeriodDates(updatedCalculation);

        // Get sanitized dates
        const startDate =
          cleanedCalculation.notice_period_start_date ||
          format(new Date(), "yyyy-MM-dd");
        const endDate =
          cleanedCalculation.notice_period_end_date ||
          format(new Date(), "yyyy-MM-dd");

        console.log(
          "Sending termination date update with dates:",
          startDate,
          endDate,
        );

        // The backend expects these fields at the top level, not nested under noticeCalculation
        updateTermination(currentTermination.id, {
          termination_date: currentTermination.termination_date, // Use the current termination date
          notice_period_start_date: startDate,
          notice_period_end_date: endDate,
          is_payment_in_lieu_of_notice:
            cleanedCalculation.paymentInLieuOfNotice,
          notice_calculation: {
            requiredNoticeDays: cleanedCalculation.requiredNoticeDays,
            payThroughDate: cleanedCalculation.payThroughDate,
            workingNoticePeriod: cleanedCalculation.workingNoticePeriod,
            paymentInLieuOfNotice: cleanedCalculation.paymentInLieuOfNotice,
          },
        });
      }
    };

  const handleNoticeDaysChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const days = parseInt(event.target.value) || 0;
    if (days < 0) return;

    if (!currentTermination?.termination_date) return;

    // Use the exact termination date string from the context
    const terminationDateStr = currentTermination.termination_date;

    const updatedCalculation = {
      ...noticeCalculation,
      requiredNoticeDays: days,
    };

    if (updatedCalculation.workingNoticePeriod) {
      // For Working Notice, pay through date = termination date exactly
      updatedCalculation.payThroughDate = terminationDateStr;
      updatedCalculation.notice_period_end_date = terminationDateStr;
    } else if (updatedCalculation.paymentInLieuOfNotice) {
      // For PILON, pay through date = termination date + notice days
      // Use a fixed noon time (UTC) to avoid timezone issues
      const terminationDate = new Date(`${terminationDateStr}T12:00:00Z`);
      const payThroughDate = format(
        addDays(terminationDate, days),
        "yyyy-MM-dd",
      );
      updatedCalculation.payThroughDate = payThroughDate;
      updatedCalculation.notice_period_end_date = payThroughDate;
    }

    setNoticeCalculation(updatedCalculation);

    if (currentTermination?.id) {
      // Ensure proper notice period dates
      const cleanedCalculation = ensureNoticePeriodDates(updatedCalculation);

      // Get sanitized dates
      const startDate =
        cleanedCalculation.notice_period_start_date ||
        format(new Date(), "yyyy-MM-dd");
      const endDate =
        cleanedCalculation.notice_period_end_date ||
        format(new Date(), "yyyy-MM-dd");

      console.log("Sending notice days update with dates:", startDate, endDate);

      // The backend expects these fields at the top level, not nested under noticeCalculation
      updateTermination(currentTermination.id, {
        termination_date: currentTermination.termination_date, // Use the current termination date
        notice_period_start_date: startDate,
        notice_period_end_date: endDate,
        is_payment_in_lieu_of_notice: cleanedCalculation.paymentInLieuOfNotice,
        notice_calculation: {
          requiredNoticeDays: cleanedCalculation.requiredNoticeDays,
          payThroughDate: cleanedCalculation.payThroughDate,
          workingNoticePeriod: cleanedCalculation.workingNoticePeriod,
          paymentInLieuOfNotice: cleanedCalculation.paymentInLieuOfNotice,
        },
      });
    }
  };

  const handlePayThroughDateChange = (date: Date | undefined) => {
    if (!date || !currentTermination?.termination_date) return;

    // Ensure consistent date objects for calculation with fixed noon time
    const terminationDate = new Date(
      currentTermination.termination_date + "T12:00:00",
    );

    // Use a date with noon time to avoid timezone issues
    const selectedDate = new Date(date);
    selectedDate.setHours(12, 0, 0, 0);

    const requiredNoticeDays = differenceInDays(selectedDate, terminationDate);

    if (requiredNoticeDays < 0) return; // Can't have a negative notice period

    // Get a consistent YYYY-MM-DD string
    const formattedPayThroughDate = selectedDate.toISOString().split("T")[0];

    const updatedCalculation = {
      ...noticeCalculation,
      requiredNoticeDays,
      payThroughDate: formattedPayThroughDate,
    };

    // Update notice period end date if working notice period is selected
    if (updatedCalculation.workingNoticePeriod) {
      updatedCalculation.notice_period_end_date = formattedPayThroughDate;
    }

    setNoticeCalculation(updatedCalculation);

    if (currentTermination?.id) {
      // Ensure proper notice period dates
      const cleanedCalculation = ensureNoticePeriodDates(updatedCalculation);

      // Get sanitized dates
      const startDate =
        cleanedCalculation.notice_period_start_date ||
        new Date().toISOString().split("T")[0];
      const endDate =
        cleanedCalculation.notice_period_end_date ||
        new Date().toISOString().split("T")[0];

      console.log(
        "Sending pay through date update with dates:",
        startDate,
        endDate,
      );

      // The backend expects these fields at the top level, not nested under noticeCalculation
      updateTermination(currentTermination.id, {
        termination_date: currentTermination.termination_date, // Use the current termination date
        notice_period_start_date: startDate,
        notice_period_end_date: endDate,
        is_payment_in_lieu_of_notice: cleanedCalculation.paymentInLieuOfNotice,
        notice_calculation: {
          requiredNoticeDays: cleanedCalculation.requiredNoticeDays,
          payThroughDate: cleanedCalculation.payThroughDate,
          workingNoticePeriod: cleanedCalculation.workingNoticePeriod,
          paymentInLieuOfNotice: cleanedCalculation.paymentInLieuOfNotice,
        },
      });
    }
  };

  // Calculate minimum date (can't be before start date)
  const getMinDate = () => {
    if (!employeeStartDate) return undefined;
    // Use noon to avoid timezone issues
    return new Date(`${employeeStartDate}T12:00:00`);
  };

  // Update getMinTerminationDate to prevent retroactive termination
  const getMinTerminationDate = () => {
    // Today with noon time to avoid timezone issues
    const today = new Date();
    today.setHours(12, 0, 0, 0);

    // Default min date is either employment start date or today, whichever is later
    const employmentStartMinDate = getMinDate();
    const baseMinDate =
      employmentStartMinDate && employmentStartMinDate > today
        ? employmentStartMinDate
        : today;

    // If Working Notice is selected, termination date must be at least requiredNoticeDays in the future
    if (
      noticeCalculation.workingNoticePeriod &&
      noticeCalculation.requiredNoticeDays
    ) {
      // For working notice, termination date must be at least requiredNoticeDays in the future
      const minDateForNotice = addDays(
        today,
        noticeCalculation.requiredNoticeDays,
      );

      // Return the later of base min date or notice period constraint
      return minDateForNotice > baseMinDate ? minDateForNotice : baseMinDate;
    }

    // For PILON, termination date must be today or later (can't terminate retroactively)
    return baseMinDate;
  };

  return (
    <div className="space-y-8">
      {/* Termination Details Section */}
      <div>
        <h3 className="text-lg font-medium mb-3">Termination Details</h3>
        <p className="mb-4">
          Please provide the details of the employee's termination. This
          information will be used to calculate the notice period and final pay.
        </p>

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="terminationReason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reason for Termination</FormLabel>
                <Select
                  value={currentTermination?.termination_reason || ""}
                  onValueChange={handleTerminationReasonChange}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {TERMINATION_REASONS.map((reason) => (
                      <SelectItem key={reason.id} value={reason.id}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="termination_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Termination Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "pl-3 text-left font-normal",
                          !currentTermination?.termination_date &&
                            "text-muted-foreground",
                        )}
                      >
                        {currentTermination?.termination_date ? (
                          format(
                            new Date(
                              currentTermination.termination_date + "T12:00:00",
                            ),
                            "PPP",
                          )
                        ) : (
                          <span>Select date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="p-2 bg-blue-50 text-sm text-blue-700 border-b border-blue-200">
                      {noticeCalculation.workingNoticePeriod ? (
                        <p>
                          Termination date must be at least{" "}
                          {noticeCalculation.requiredNoticeDays} days in the
                          future
                        </p>
                      ) : (
                        <p>Termination date must be today or in the future</p>
                      )}
                    </div>
                    <Calendar
                      mode="single"
                      selected={
                        currentTermination?.termination_date
                          ? new Date(
                              currentTermination.termination_date + "T12:00:00",
                            )
                          : undefined
                      }
                      onSelect={handleTerminationDateChange}
                      disabled={(date) => {
                        const minDate = getMinTerminationDate();
                        return minDate ? date < minDate : false;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {noticeCalculation.workingNoticePeriod &&
                  noticeCalculation.requiredNoticeDays > 0 && (
                    <FormDescription>
                      For working notice periods, the termination date must be
                      at least {noticeCalculation.requiredNoticeDays} days from
                      today.
                    </FormDescription>
                  )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {currentTermination?.termination_reason && (
          <div className="mt-6 p-4 bg-blue-50 rounded-md">
            <h3 className="font-medium text-blue-800">Legal Notes</h3>
            <p className="text-sm text-blue-700 mt-2">
              Please ensure all appropriate documentation for this termination
              has been completed and retained for your records.
            </p>
          </div>
        )}
      </div>

      {/* Notice Period Section */}
      {currentTermination?.termination_date && (
        <div>
          <h3 className="text-lg font-medium mb-3">Notice Period Details</h3>
          <p className="mb-4">
            Calculate the notice period based on the employee's length of
            service and contract terms. UK law requires a minimum notice period
            of one week for each year of service (up to 12 weeks).
          </p>

          <Card>
            <CardHeader>
              <CardTitle>Notice Period Calculation</CardTitle>
              <CardDescription>
                Calculate the required notice period and final working date
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border rounded-md p-4 mb-4 space-y-3">
                <FormLabel className="block mb-2">Notice Options</FormLabel>
                <div className="text-xs text-blue-600 mb-3">
                  <p>
                    <strong>Working Notice Period:</strong> The employee works
                    until the termination date (must be{" "}
                    {noticeCalculation.requiredNoticeDays} days from today)
                  </p>
                  <p>
                    <strong>PILON:</strong> The employee leaves on the
                    termination date but is paid for{" "}
                    {noticeCalculation.requiredNoticeDays} more days
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <FormField
                    control={form.control}
                    name="workingNoticePeriod"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            id="workingNoticePeriod"
                            checked={noticeCalculation.workingNoticePeriod}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                              handleCheckboxChange("workingNoticePeriod")(
                                !!checked,
                              );
                            }}
                          />
                        </FormControl>
                        <label
                          htmlFor="workingNoticePeriod"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Working Notice Period
                        </label>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <FormField
                    control={form.control}
                    name="paymentInLieuOfNotice"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            id="paymentInLieuOfNotice"
                            checked={noticeCalculation.paymentInLieuOfNotice}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                              handleCheckboxChange("paymentInLieuOfNotice")(
                                !!checked,
                              );
                            }}
                          />
                        </FormControl>
                        <label
                          htmlFor="paymentInLieuOfNotice"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Payment in Lieu of Notice (PILON)
                        </label>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <FormField
                control={form.control}
                name="requiredNoticeDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Required Notice (days)</FormLabel>
                    <FormControl>
                      <Input
                        step={1}
                        type="number"
                        min={0}
                        value={noticeCalculation.requiredNoticeDays}
                        onChange={handleNoticeDaysChange}
                      />
                    </FormControl>
                    <FormDescription>
                      The legally required notice period based on length of
                      service and contract terms
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="payThroughDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Pay Through Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "pl-3 text-left font-normal",
                              !noticeCalculation.payThroughDate &&
                                "text-muted-foreground",
                            )}
                          >
                            {noticeCalculation.payThroughDate ? (
                              format(
                                new Date(
                                  noticeCalculation.payThroughDate +
                                    "T12:00:00",
                                ),
                                "PPP",
                              )
                            ) : (
                              <span>Select date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={
                            noticeCalculation.payThroughDate
                              ? new Date(
                                  noticeCalculation.payThroughDate +
                                    "T12:00:00",
                                )
                              : undefined
                          }
                          onSelect={handlePayThroughDateChange}
                          disabled={(date) => {
                            if (!currentTermination?.termination_date)
                              return false;
                            const termDate = new Date(
                              currentTermination.termination_date + "T12:00:00",
                            );
                            return date < termDate;
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>
                      The final date through which the employee will be paid
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
            <CardFooter className="bg-gray-50 p-4 rounded-b-md">
              <div className="text-sm text-gray-600">
                <p>
                  <strong>Working Notice:</strong> Employee continues to work
                  until the termination date. The termination date must be at
                  least {noticeCalculation.requiredNoticeDays} days in the
                  future from today.
                </p>
                <p>
                  <strong>PILON:</strong> Employee's employment ends on the
                  termination date, but they receive pay for an additional{" "}
                  {noticeCalculation.requiredNoticeDays} days (their notice
                  period).
                </p>
              </div>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CombinedDetailsStep;
